import { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, ShoppingBag, CreditCard, Truck, Volume2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const notificationIcons = {
  new_order: ShoppingBag,
  payment_received: CreditCard,
  order_shipped: Truck,
  default: Bell
};

// Notification sound - LOUDER and LONGER
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // First beep - high
    const osc1 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    osc1.connect(gain1);
    gain1.connect(audioContext.destination);
    osc1.frequency.value = 880;
    osc1.type = 'sine';
    gain1.gain.setValueAtTime(0.5, audioContext.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.3, audioContext.currentTime + 0.15);
    osc1.start(audioContext.currentTime);
    osc1.stop(audioContext.currentTime + 0.15);
    
    // Second beep - higher
    const osc2 = audioContext.createOscillator();
    const gain2 = audioContext.createGain();
    osc2.connect(gain2);
    gain2.connect(audioContext.destination);
    osc2.frequency.value = 1100;
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0.5, audioContext.currentTime + 0.2);
    gain2.gain.exponentialRampToValueAtTime(0.3, audioContext.currentTime + 0.35);
    osc2.start(audioContext.currentTime + 0.2);
    osc2.stop(audioContext.currentTime + 0.35);
    
    // Third beep - highest and longest
    const osc3 = audioContext.createOscillator();
    const gain3 = audioContext.createGain();
    osc3.connect(gain3);
    gain3.connect(audioContext.destination);
    osc3.frequency.value = 1320;
    osc3.type = 'sine';
    gain3.gain.setValueAtTime(0.6, audioContext.currentTime + 0.4);
    gain3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.9);
    osc3.start(audioContext.currentTime + 0.4);
    osc3.stop(audioContext.currentTime + 0.9);
    
  } catch (e) {
    console.log('Could not play notification sound');
  }
};

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const prevUnreadCount = useRef(0);
  const hasInitialized = useRef(false);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/notifications/count`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setNotifications(notifRes.data);
      const newCount = countRes.data.unread_count;
      
      // Play sound if new notifications arrived (not on first load)
      if (hasInitialized.current && newCount > prevUnreadCount.current) {
        playNotificationSound();
      }
      
      hasInitialized.current = true;
      prevUnreadCount.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 15 seconds
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Test sound button
  const testSound = (e) => {
    e.stopPropagation();
    playNotificationSound();
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes}min`;
    if (hours < 24) return `Il y a ${hours}h`;
    return `Il y a ${days}j`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-brand-white/70 hover:text-brand-gold transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <motion.span 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold"
          >
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          </motion.span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40 bg-black/20" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="fixed left-4 right-4 top-20 sm:absolute sm:left-0 sm:right-auto sm:top-auto sm:mt-2 w-auto sm:w-[420px] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-brand-black to-gray-800">
                <h3 className="font-bold text-white flex items-center gap-2 text-lg">
                  <Bell className="h-5 w-5 text-brand-gold" />
                  Notifications
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {unreadCount} nouveau{unreadCount > 1 ? 'x' : ''}
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={testSound}
                    className="text-white/60 hover:text-brand-gold p-1"
                    title="Tester le son"
                  >
                    <Volume2 className="h-4 w-4" />
                  </button>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs text-brand-gold hover:text-brand-gold/80 flex items-center gap-1 bg-brand-gold/20 px-3 py-1.5 rounded-full font-medium"
                    >
                      <CheckCheck className="h-3 w-3" />
                      Tout lu
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-white/60 hover:text-white ml-2"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Notifications List */}
              <div className="max-h-[60vh] sm:max-h-[500px] overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-10 text-center text-gray-500">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Bell className="h-8 w-8 opacity-30" />
                    </div>
                    <p className="font-semibold text-gray-700">Aucune notification</p>
                    <p className="text-sm text-gray-400 mt-1">Les nouvelles commandes apparaîtront ici</p>
                  </div>
                ) : (
                  notifications.map((notif, index) => {
                    const Icon = notificationIcons[notif.type] || notificationIcons.default;
                    return (
                      <motion.div
                        key={notif.notification_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-all cursor-pointer ${
                          !notif.is_read ? 'bg-gradient-to-r from-amber-50 to-white border-l-4 border-l-brand-gold' : ''
                        }`}
                        onClick={() => !notif.is_read && markAsRead(notif.notification_id)}
                      >
                        <div className="flex gap-4">
                          <div className={`p-3 rounded-xl flex-shrink-0 ${
                            notif.type === 'new_order' ? 'bg-green-100 text-green-600' :
                            notif.type === 'payment_received' ? 'bg-blue-100 text-blue-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className={`text-base ${!notif.is_read ? 'font-bold text-brand-black' : 'font-medium text-gray-700'}`}>
                                  {notif.title}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                              </div>
                              {!notif.is_read && (
                                <span className="w-3 h-3 bg-brand-gold rounded-full flex-shrink-0 mt-1.5"></span>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              <p className="text-xs text-gray-400 font-medium">{formatTime(notif.created_at)}</p>
                              {notif.order_id && (
                                <Link
                                  to={`/admin/orders`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(false);
                                  }}
                                  className="text-sm text-brand-gold hover:underline font-semibold flex items-center gap-1"
                                >
                                  Voir commande →
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
              
              {/* Footer */}
              {notifications.length > 0 && (
                <div className="p-4 bg-gray-50 border-t text-center">
                  <Link 
                    to="/admin/orders" 
                    onClick={() => setIsOpen(false)}
                    className="text-sm text-brand-gold hover:text-brand-gold/80 font-bold"
                  >
                    Voir toutes les commandes →
                  </Link>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
